import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  Chip,
  Icons,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import useGetComments from '@core/comments/hooks/useGetComments';
import { getComments, getComment } from '@core/selectors/comments';
import { currentUser } from '@core/selectors/user';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import { online as isOnline } from '@core/selectors/live';
import useDeleteComment from '@core/comments/hooks/useDeleteComment';
import { AulaState } from '@core/types/state';
import formatDate from '@core/utils/formatDate';
import { UB_COMMENT_ITEM_TYPE } from '@app/utils/itemTypeForAnalytics';
import {
  ReactionList,
  ReactionPicker,
} from '@components/presentational/ResponsiveReactions';
import ResponsiveEditorReadOnly from '../ResponsiveEditorReadOnly';
import { Comment } from '../../../../Comments/types';
import { createStyles } from './ResponsivePostComments.styles';
import EditCommentModal from '../../../MaterialResponsive/MaterialEditor/Discussion/EditCommentModal.container';
import DeleteCommentModal from '../../../MaterialResponsive/MaterialEditor/Discussion/DeleteCommentModal';

const INITIAL_VISIBLE_COMMENTS = 5;

type ResponsivePostCommentsProps = {
  postId: string;
  readOnly?: boolean;
};

const getName = (user?: Comment['user']) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown user';

const CommentContent = ({ comment }: { comment: Comment }) => {
  if (!comment.content) return null;

  try {
    const content = JSON.parse(comment.content);
    return (
      <ResponsiveEditorReadOnly
        key={comment.editedAt || comment.id}
        itemId={comment.id}
        editorState={content}
        itemType="CommonRoom"
      />
    );
  } catch {
    return <Typography component="p">{comment.content}</Typography>;
  }
};

const CommentItem = ({
  id,
  isReply = false,
  readOnly = false,
}: {
  id: string;
  isReply?: boolean;
  readOnly?: boolean;
}) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDark = theme.palette.mode === 'dark';
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [nameRef, isNameOverflowing] = useIsOverflowing<HTMLDivElement>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const comment = useSelector((state: AulaState) => getComment(state, id)) as
    | Comment
    | undefined;
  const user = useSelector(currentUser);
  const authorIsEducator = useSelector((state: AulaState) =>
    isInstructor(state, comment?.user?.objectId)
  );
  const currentUserIsEducator = useSelector((state: AulaState) =>
    isInstructor(state, user?.objectId)
  );
  const isStaffAccess = useSelector(accessingCurrentSpaceAsStaff);
  const isCommentAuthorOnline = useSelector((state: AulaState) =>
    isOnline(state, comment?.user?.objectId)
  );

  const isAuthor = user?.objectId === comment?.user?.objectId;
  const canEdit = isAuthor;
  const canDelete = isAuthor || currentUserIsEducator;
  const showMenu =
    !readOnly && (canEdit || canDelete) && (!isStaffAccess || isAuthor);
  const { mutate: handleDelete, isLoading: isDeleting } = useDeleteComment(
    comment || {}
  );

  if (!comment?.user) return null;

  const name = getName(comment.user);
  const initials =
    (comment.user.firstName?.charAt(0) || 'U').toUpperCase() +
    (comment.user.lastName?.charAt(0) || '').toUpperCase();

  const handleMenuClose = () => setAnchorEl(null);
  const handleEdit = () => {
    handleMenuClose();
    setEditOpen(true);
  };
  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteOpen(true);
  };
  const handleConfirmDelete = () => {
    handleDelete(undefined, { onSuccess: () => setDeleteOpen(false) });
  };

  return (
    <Box
      component="article"
      aria-label={`Comment by ${name}`}
      sx={styles.comment}
    >
      <Box sx={styles.commentHeader}>
        <Box sx={styles.authorRow}>
          <Box sx={styles.avatarContainer}>
            <Box sx={styles.avatarPresenceWrapper}>
              <Avatar
                altText={name}
                fallbackText={initials}
                kind="circular"
                src={comment.user.avatar}
                sx={styles.avatar}
              />
              {isCommentAuthorOnline && !isAuthor && (
                <Box sx={styles.onlineBadge} />
              )}
            </Box>
          </Box>
          <Box sx={styles.authorDetails}>
            <Box sx={styles.authorNameRow}>
              <Tooltip
                placement="top"
                title={name}
                disableFocusListener={!isNameOverflowing || !isDesktop}
                disableHoverListener={!isNameOverflowing || !isDesktop}
                disableTouchListener={!isNameOverflowing || !isDesktop}
              >
                <Box ref={nameRef} component="div" sx={styles.authorName}>
                  {name}
                </Box>
              </Tooltip>
              {authorIsEducator && (
                <Box sx={styles.educatorBadge}>
                  <Chip
                    label="Educator"
                    size="small"
                    sx={styles.educatorChip}
                  />
                </Box>
              )}
            </Box>
            <Typography sx={styles.date}>
              {formatDate(comment.createdAt)}
              {comment.editedAt ? ' (Edited)' : ''}
            </Typography>
          </Box>
        </Box>
        {showMenu && (
          <>
            <Tooltip title="Comment options" position="top">
              <span>
                <IconButton
                  aria-label="Comment options"
                  size="small"
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  sx={styles.moreButton}
                >
                  <Icons.MoreVert sx={styles.moreIcon} />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={styles.commentMenu(isDark)}
            >
              {canEdit && <MenuItem label="Edit" onClick={handleEdit} />}
              {canDelete && (
                <MenuItem
                  label="Delete"
                  onClick={handleDeleteClick}
                  sx={{ color: theme.palette.error.main }}
                />
              )}
            </Menu>
          </>
        )}
      </Box>
      <Box sx={styles.commentContent}>
        <CommentContent comment={comment} />
      </Box>
      {readOnly ? (
        <ReactionList
          hideWhenNoReactions
          itemId={comment.id}
          itemType={UB_COMMENT_ITEM_TYPE}
          readOnly
          sx={{ pl: 6 }}
        />
      ) : (
        <Box aria-label="Comment reactions and actions" sx={styles.reactions}>
          <ReactionList
            hideWhenNoReactions
            itemId={comment.id}
            itemType={UB_COMMENT_ITEM_TYPE}
            readOnly={false}
          />
          <ReactionPicker itemId={comment.id} itemType={UB_COMMENT_ITEM_TYPE} />
          <Box component="span" sx={styles.reactionSpacer} />
          <Box component="span" sx={styles.replyAction}>
            <Icons.Reply aria-hidden="true" sx={styles.replyIcon} />
            <Typography sx={styles.replyLabel}>Reply</Typography>
          </Box>
        </Box>
      )}
      {!isReply && comment.replies?.length > 0 && (
        <Box sx={styles.replies}>
          {comment.replies.map((replyId) => (
            <Box key={replyId} sx={styles.reply}>
              <CommentItem id={replyId} isReply readOnly={readOnly} />
            </Box>
          ))}
        </Box>
      )}
      <DeleteCommentModal
        open={deleteOpen}
        loading={isDeleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      {!readOnly && (
        <EditCommentModal
          open={editOpen}
          comment={comment}
          entityType="post"
          entityId={comment.entityId}
          onClose={() => setEditOpen(false)}
        />
      )}
    </Box>
  );
};

const ResponsivePostComments = ({
  postId,
  readOnly = false,
}: ResponsivePostCommentsProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COMMENTS);
  const query = useGetComments(CommentsEntityTypes.post, postId);
  const comments = useSelector((state: AulaState) =>
    getComments(state, postId, CommentsEntityTypes.post)
  ) as string[];
  const user = useSelector(currentUser);
  const commentIds = query.isSuccess
    ? comments
    : query.data?.commentsIds || comments;
  const visibleIds = expanded
    ? commentIds.slice(Math.max(0, commentIds.length - visibleCount))
    : [];
  const hiddenCount = commentIds.length - visibleCount;
  const userName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'You';

  const toggleComments = () => setExpanded((value) => !value);

  return (
    <Box data-testid="responsive-post-comments" sx={styles.root}>
      {commentIds.length > 0 && (
        <Box
          component="button"
          onClick={toggleComments}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? 'Hide comments'
              : `Show ${commentIds.length} comment${
                  commentIds.length === 1 ? '' : 's'
                }`
          }
          sx={styles.toggleButton}
        >
          {expanded
            ? 'Hide comments'
            : `Show ${commentIds.length} comment${
                commentIds.length === 1 ? '' : 's'
              }`}
          <Icons.KeyboardArrowDown sx={styles.toggleIcon(expanded)} />
        </Box>
      )}

      {expanded && (
        <Box sx={styles.comments}>
          {hiddenCount > 0 && (
            <Box
              component="button"
              onClick={() => setVisibleCount(commentIds.length)}
              sx={styles.previousCommentsButton}
            >
              See previous comments
            </Box>
          )}
          {visibleIds.map((id, index) => (
            <Box
              key={id}
              sx={styles.commentWrapper(index === visibleIds.length - 1)}
            >
              <CommentItem id={id} readOnly={readOnly} />
            </Box>
          ))}
        </Box>
      )}

      {!readOnly && (
        <Box
          aria-label="Add a comment"
          sx={styles.addComment(commentIds.length > 0)}
        >
          <Avatar
            altText={userName}
            fallbackText={(user?.firstName?.charAt(0) || 'Y').toUpperCase()}
            kind="circular"
            src={user?.avatar}
            sx={styles.addCommentAvatar}
          />
          <Box sx={styles.addCommentInput}>Add a comment</Box>
        </Box>
      )}
    </Box>
  );
};

export default ResponsivePostComments;
